import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import VoucherImageUpload from "components/Common/VoucherImageUpload"
import { useMemo, useCallback, useState, useEffect } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button, Row, Col, Card, CardBody } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { getVehicleImporters } from "api/vehicleImporterApi"
import { useAccountPaymentMethods } from "hooks/useAccountPaymentMethod"
import { useAccounts } from "hooks/useAccountEntity"
import { useAccountVehiclePurchases } from "hooks/useAccountVehiclePurchase"
import AccountVehiclePurchaseAddEdit from "pages/AccountPages/AccountVehiclePurchase/AccountVehiclePurchaseAddEdit"
import AccountPaymentMethodAddEdit from "pages/AccountPages/AccountPaymentMethod/AccountPaymentMethodAddEdit"
import AccountAddEdit from "pages/AccountPages/Account/AccountAddEdit"
import ImporterOnboardingModal from "pages/VehicleImporterPages/VehicleImporterList/ImporterOnboardingModal"
import {
  useCreateAccountImporterPayment,
  useEditAccountImporterPayment,
} from "hooks/useAccountImporterPayment"

const IMPORTER_QUERY_KEY = ["vehicle-importers", "account-importer-payment"]

const AccountImporterPaymentAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const queryClient = useQueryClient()
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAccountImporterPayment()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAccountImporterPayment()

  const [voucherImages, setVoucherImages] = useState(editedData?.images || [])
  const [purchaseModal, setPurchaseModal] = useState(false)
  const [importerModal, setImporterModal] = useState(false)
  const [paymentMethodModal, setPaymentMethodModal] = useState(false)
  const [accountModal, setAccountModal] = useState(false)

  useEffect(() => {
    setVoucherImages(editedData?.images || [])
  }, [editedData])

  const { data: importerResponse, isLoading: importerLoading } = useQuery({
    queryKey: IMPORTER_QUERY_KEY,
    queryFn: () => getVehicleImporters({ all: true, per_page: 200 }),
    enabled: modal,
  })

  const { data: purchaseResponse, isLoading: purchaseLoading } = useAccountVehiclePurchases(
    { all: true, per_page: 200 },
    modal
  )

  const { data: paymentMethodResponse, isLoading: paymentMethodLoading } =
    useAccountPaymentMethods({ all: true, per_page: 200 }, modal)

  const { data: accountResponse, isLoading: accountLoading } =
    useAccounts({ all: true, per_page: 200 }, modal)

  const importers = importerResponse?.data?.data || importerResponse?.data || []
  const purchases = purchaseResponse?.data?.data || purchaseResponse?.data || []
  const paymentMethods = paymentMethodResponse?.data?.data || paymentMethodResponse?.data || []
  const accounts = accountResponse?.data?.data || accountResponse?.data || []

  const importerOptions = useMemo(
    () => (Array.isArray(importers) ? importers.map(item => ({ value: item.id, label: item.name })) : []),
    [importers]
  )

  const purchaseOptions = useMemo(
    () =>
      Array.isArray(purchases)
        ? purchases.map(item => ({
            value: item.id,
            label: `${item.vehicle?.stock_id || "Vehicle"} - ${Number(item.purchase_price || 0).toLocaleString()}`,
          }))
        : [],
    [purchases]
  )

  const paymentMethodOptions = useMemo(
    () =>
      Array.isArray(paymentMethods)
        ? paymentMethods.map(item => ({ value: item.id, label: item.name }))
        : [],
    [paymentMethods]
  )

  const accountOptions = useMemo(
    () => (Array.isArray(accounts) ? accounts.map(item => ({ value: item.id, label: item.name })) : []),
    [accounts]
  )

  const validationSchema = Yup.object({
    vehicle_importer_id: Yup.mixed().required("Please select an importer"),
    vehicle_purchase_id: Yup.mixed().nullable(),
    amount: Yup.number().typeError("Amount must be numeric").integer("Amount must be a whole number").min(1, "Amount must be at least 1").required("Please enter payment amount"),
    payment_method_id: Yup.mixed().required("Please select a payment method"),
    account_id: Yup.mixed().required("Please select an account"),
    payment_date: Yup.string().required("Please select payment date"),
    reference_number: Yup.string().nullable().max(255, "Reference number cannot exceed 255 characters"),
    notes: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        ...values,
        amount: Number(values.amount),
        reference_number: values.reference_number?.trim() || null,
        notes: values.notes?.trim() || null,
        image_ids: voucherImages.map(img => img.id),
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(res?.message ?? `Importer payment ${isEdit ? "updated" : "created"} successfully.`)
        resetForm()
        toggleModal()
      } catch (error) {
        const msg = error?.response?.data?.message || "Failed to save importer payment."
        toast.error(msg)
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal, voucherImages]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vehicle_importer_id: editedData?.vehicle_importer_id || editedData?.vehicleImporter?.id || "",
      vehicle_purchase_id: editedData?.vehicle_purchase_id || editedData?.vehiclePurchase?.id || "",
      amount: editedData?.amount ?? "",
      payment_method_id: editedData?.payment_method_id || editedData?.paymentMethod?.id || "",
      account_id: editedData?.account_id || editedData?.account?.id || "",
      payment_date: editedData?.payment_date || "",
      reference_number: editedData?.reference_number || "",
      notes: editedData?.notes || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const loadingAny = createLoading || editLoading || importerLoading || purchaseLoading || paymentMethodLoading || accountLoading

  const handlePurchaseCreated = useCallback(
    purchase => {
      if (purchase?.id) {
        formik.setFieldValue("vehicle_purchase_id", purchase.id)
      }
    },
    [formik]
  )

  const handleImporterOnboarded = useCallback(
    newImporter => {
      queryClient.invalidateQueries({ queryKey: IMPORTER_QUERY_KEY })
      if (newImporter?.id) {
        formik.setFieldValue("vehicle_importer_id", newImporter.id)
      }
    },
    [formik, queryClient]
  )

  const handlePaymentMethodCreated = useCallback(
    paymentMethod => {
      if (paymentMethod?.id) {
        formik.setFieldValue("payment_method_id", paymentMethod.id)
      }
    },
    [formik]
  )

  const handleAccountCreated = useCallback(
    account => {
      if (account?.id) {
        formik.setFieldValue("account_id", account.id)
      }
    },
    [formik]
  )

  return (
    <>
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Importer Payment" : "Add Importer Payment"}
      size="xl"
    >
      <Form onSubmit={e => { e.preventDefault(); formik.handleSubmit() }}>
        <Card className="border">
          <CardBody>
            <h5 className="mb-3">Payment Information</h5>
            <Row>
              <Col md={6}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Label htmlFor="vehicle_importer_id" className="mb-0">
                    Importer <span className="text-danger">*</span>
                  </Label>
                  <Button
                    color="link"
                    size="sm"
                    type="button"
                    className="p-0 text-decoration-none"
                    onClick={() => setImporterModal(true)}
                  >
                    + Onboard New Importer
                  </Button>
                </div>
                <FormikCreatableSelect name="vehicle_importer_id" formik={formik} options={importerOptions} loading={importerLoading} />
              </Col>
              <Col md={6}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Label htmlFor="vehicle_purchase_id" className="mb-0">Related Purchase (optional)</Label>
                  <Button
                    color="link"
                    size="sm"
                    type="button"
                    className="p-0 text-decoration-none"
                    onClick={() => setPurchaseModal(true)}
                  >
                    + Add Vehicle Purchase
                  </Button>
                </div>
                <FormikCreatableSelect name="vehicle_purchase_id" formik={formik} options={purchaseOptions} loading={purchaseLoading} />
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <div className="mb-3">
                  <Label htmlFor="amount">Amount</Label>
                  <InputField type="number" name="amount" id="amount" min="1" value={formik.values.amount} onChange={formik.handleChange} invalid={formik.touched.amount && !!formik.errors.amount} />
                  {formik.touched.amount && formik.errors.amount && <FormFeedback type="invalid">{formik.errors.amount}</FormFeedback>}
                </div>
              </Col>
              <Col md={4}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Label htmlFor="payment_method_id" className="mb-0">
                    Payment Method <span className="text-danger">*</span>
                  </Label>
                  <Button
                    color="link"
                    size="sm"
                    type="button"
                    className="p-0 text-decoration-none"
                    onClick={() => setPaymentMethodModal(true)}
                  >
                    + Add New
                  </Button>
                </div>
                <FormikCreatableSelect name="payment_method_id" formik={formik} options={paymentMethodOptions} loading={paymentMethodLoading} />
              </Col>
              <Col md={4}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Label htmlFor="account_id" className="mb-0">
                    Account <span className="text-danger">*</span>
                  </Label>
                  <Button
                    color="link"
                    size="sm"
                    type="button"
                    className="p-0 text-decoration-none"
                    onClick={() => setAccountModal(true)}
                  >
                    + Add New
                  </Button>
                </div>
                <FormikCreatableSelect name="account_id" formik={formik} options={accountOptions} loading={accountLoading} />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="payment_date">Payment Date</Label>
                  <InputField type="date" name="payment_date" id="payment_date" value={formik.values.payment_date} onChange={formik.handleChange} invalid={formik.touched.payment_date && !!formik.errors.payment_date} />
                  {formik.touched.payment_date && formik.errors.payment_date && <FormFeedback type="invalid">{formik.errors.payment_date}</FormFeedback>}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="reference_number">Reference Number</Label>
                  <InputField type="text" name="reference_number" id="reference_number" value={formik.values.reference_number} onChange={formik.handleChange} invalid={formik.touched.reference_number && !!formik.errors.reference_number} />
                  {formik.touched.reference_number && formik.errors.reference_number && <FormFeedback type="invalid">{formik.errors.reference_number}</FormFeedback>}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="border mt-3">
          <CardBody>
            <h5 className="mb-3">Additional Details</h5>
            <div className="mb-3">
              <Label htmlFor="notes">Notes</Label>
              <InputField type="textarea" name="notes" id="notes" value={formik.values.notes} onChange={formik.handleChange} invalid={formik.touched.notes && !!formik.errors.notes} />
              {formik.touched.notes && formik.errors.notes && <FormFeedback type="invalid">{formik.errors.notes}</FormFeedback>}
            </div>
            <VoucherImageUpload images={voucherImages} onChange={setVoucherImages} />
          </CardBody>
        </Card>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={loadingAny}>
            {loadingAny ? "Saving..." : isEdit ? "Save Changes" : "Add Importer Payment"}
          </Button>
        </div>
      </Form>
    </ThemeModal>

    <AccountVehiclePurchaseAddEdit
      modal={purchaseModal}
      toggleModal={() => setPurchaseModal(false)}
      isEdit={false}
      editedData={null}
      onSuccess={handlePurchaseCreated}
    />

    <ImporterOnboardingModal
      modal={importerModal}
      toggleModal={() => setImporterModal(false)}
      onSuccess={handleImporterOnboarded}
    />

    <AccountPaymentMethodAddEdit
      modal={paymentMethodModal}
      toggleModal={() => setPaymentMethodModal(false)}
      isEdit={false}
      editedData={null}
      onSuccess={handlePaymentMethodCreated}
    />

    <AccountAddEdit
      modal={accountModal}
      toggleModal={() => setAccountModal(false)}
      isEdit={false}
      editedData={null}
      onSuccess={handleAccountCreated}
    />
    </>
  )
}

export default AccountImporterPaymentAddEdit
